// Bandeja compartida entre la página /notifications y el desplegable de la campana.
import type { ReactNode } from 'react';
import { toAppError } from '../../data/errors.ts';
import { NOTIFICATION_LABELS, type InboxItem, type NotificationType } from '../../data/notifications.ts';
import { formatDateTime } from '../../domain/dates.ts';
import { WORK_TYPE_LABELS } from '../../domain/orders.ts';
import { CheckCircleIcon, OrdersIcon, PackageIcon, RouteIcon, UndoIcon } from '../../ui/icons.tsx';
import type { useInbox } from './use-inbox.ts';

const TYPE_ICONS: Record<NotificationType, ReactNode> = {
  assignment: <OrdersIcon />,
  parts_dispatched: <RouteIcon />,
  parts_requested: <PackageIcon />,
  work_done: <CheckCircleIcon />,
  work_returned: <UndoIcon />,
};

interface InboxListProps {
  inbox: ReturnType<typeof useInbox>['inbox'];
  onOpen: (item: InboxItem) => void;
  limit?: number;
}

/** "Nueva orden asignada" muestra el Tipo Garantía; los demás avisos, "OS X". */
function inboxReference(item: InboxItem): string {
  return item.type === 'assignment' ? WORK_TYPE_LABELS[item.work_type] : `OS ${item.folio}`;
}

export function InboxList({ inbox, onOpen, limit }: InboxListProps) {
  if (inbox.isPending)
    return (
      <p className="inbox-state" aria-busy="true">
        Cargando alertas…
      </p>
    );
  if (inbox.isError) return <p className="inbox-state">{toAppError(inbox.error).message}</p>;
  if (inbox.data.length === 0) {
    return (
      <p className="inbox-state inbox-empty">
        <CheckCircleIcon />
        No tienes alertas.
      </p>
    );
  }
  const items = limit ? inbox.data.slice(0, limit) : inbox.data;
  return (
    <ul className="inbox">
      {items.map((item) => (
        <li key={item.id}>
          <button
            type="button"
            className={`inbox-item inbox-${item.type}${item.read_at ? '' : ' inbox-unread'}`}
            onClick={() => onOpen(item)}
          >
            <span className="inbox-icon">{TYPE_ICONS[item.type]}</span>
            <span className="inbox-text">
              <span className="inbox-title">{NOTIFICATION_LABELS[item.type]}</span>
              <span className="inbox-meta">
                {inboxReference(item)} · {formatDateTime(item.created_at)}
              </span>
            </span>
            {item.read_at ? null : <span className="dot" aria-label="No leído" />}
          </button>
        </li>
      ))}
    </ul>
  );
}
